import React, { useCallback } from 'react'
import {
  Grid,
  MenuItem,
  TextField,
  CircularProgress,
  Menu,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import Button from '@mui/material/Button'
import ClearIcon from '@mui/icons-material/Clear'

import { validateMinMaxValue } from '../../../../../utils/validateMinMaxValue'
import DateRangePicker from '../../../../../components/DateRangePicker'
import dayjs from 'dayjs'
import ProductSelectSearch from '../../../../../components/SelectSearch/ProductSelectSearch'
import CategorySelectSearch from '../../../../../components/SelectSearch/CategorySelectSearch'

export interface IFilterMapped {
  page?: number
  perPage?: number
  startDate?: string
  endDate?: string
  minTotal?: number
  maxTotal?: number
  sortBy?: string
  search?: string
  productId?: string
  categoryId?: string
}

export type CustomerReportExportType = 'customerWithOrders' | 'customerList'

interface FilterProps {
  filter: IFilterMapped
  onFilterChange: (filter: IFilterMapped) => void
  onSearch: (filter: IFilterMapped) => void
  /** ดาวน์โหลด Excel — ถ้าไม่ส่งจะปิดการใช้งานปุ่ม */
  onDownloadExcel?: (type: CustomerReportExportType) => void
  /** ปิดปุ่มชั่วคราว (เช่น ไม่มีข้อมูล) นอกเหนือจากสถานะโหลด */
  downloadDisabled?: boolean
  isExporting?: boolean
}

const CustomerReportFilter = ({
  onFilterChange,
  filter,
  onSearch,
  onDownloadExcel,
  downloadDisabled = false,
  isExporting = false,
}: FilterProps) => {
  const { page, perPage, startDate, endDate, minTotal, maxTotal, search } =
    filter
  const [exportAnchorEl, setExportAnchorEl] =
    React.useState<null | HTMLElement>(null)
  const isExportMenuOpen = Boolean(exportAnchorEl)

  const handleReset = useCallback(() => {
    onFilterChange({
      search: undefined,
      page: page,
      perPage: perPage,
      startDate: dayjs().subtract(30, 'days').format(),
      endDate: dayjs().format(),
      minTotal: undefined,
      maxTotal: undefined,
    })

    onSearch({
      search: undefined,
      page: page,
      perPage: perPage,
      startDate: dayjs().subtract(30, 'days').format(),
      endDate: dayjs().format(),
      minTotal: undefined,
      maxTotal: undefined,
    })
  }, [startDate, endDate, minTotal, maxTotal, search, onFilterChange, onSearch])

  const handleFilterChange = useCallback(
    (newFilter: IFilterMapped) => {
      onFilterChange({ ...filter, ...newFilter })
    },
    [filter, onFilterChange],
  )

  const handleOpenExportMenu = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      setExportAnchorEl(event.currentTarget)
    },
    [],
  )

  const handleCloseExportMenu = useCallback(() => {
    setExportAnchorEl(null)
  }, [])

  const handleSelectExport = useCallback(
    (type: CustomerReportExportType) => {
      onDownloadExcel?.(type)
      handleCloseExportMenu()
    },
    [handleCloseExportMenu, onDownloadExcel],
  )

  return (
    <Grid container spacing={2} mb={2}>
      <Grid item xs={12} lg={2}>
        <TextField
          fullWidth
          id="search"
          name="search"
          label={'ค้นหา'}
          value={filter.search || ''}
          onChange={(e) =>
            handleFilterChange({
              search: e.target.value,
            })
          }
        />
      </Grid>
      <Grid item xs={12} lg={4}>
        <DateRangePicker
          startDate={startDate}
          endDate={endDate}
          onChange={(startDate, endDate) => {
            handleFilterChange({ startDate, endDate })
          }}
        />
      </Grid>
      <Grid item xs={12} lg={3}>
        <CategorySelectSearch
          multiple={false}
          onSelect={(e) => {
            handleFilterChange({ categoryId: e?.value?.objectId })
          }}
        />
      </Grid>
      <Grid item xs={12} lg={3}>
        <ProductSelectSearch
          multiple={false}
          onSelect={(e) => {
            handleFilterChange({ productId: e?.value?.objectId })
          }}
        />
      </Grid>

      <Grid item xs={12} lg={2}>
        <TextField
          fullWidth
          id="minTotal"
          name="minTotal"
          label={'ช่วงราคา (น้อยที่สุด)'}
          variant={'filled'}
          type={'number'}
          value={filter.minTotal || ''}
          onChange={(e) =>
            handleFilterChange({
              minTotal:
                validateMinMaxValue(e.target.value, 0, 999999) || undefined,
            })
          }
        />
      </Grid>
      <Grid item xs={12} lg={2}>
        <TextField
          fullWidth
          id="maxTotal"
          name="maxTotal"
          label={'ช่วงราคา (มากที่สุด)'}
          variant={'filled'}
          type={'number'}
          value={filter.maxTotal || ''}
          onChange={(e) =>
            handleFilterChange({
              maxTotal:
                validateMinMaxValue(e.target.value, 0, 999999) || undefined,
            })
          }
        />
      </Grid>
      <Grid item xs={12} lg={2}>
        <TextField
          fullWidth
          id="sortBy"
          name="sortBy"
          label={'เรียงลำดับข้อมูล'}
          value={filter.sortBy || 'total'}
          onChange={(e) =>
            handleFilterChange({
              sortBy: e.target.value,
            })
          }
          select
        >
          <MenuItem value="total">จำนวนยอดซื้อ</MenuItem>
          <MenuItem value="order">จำนวนคำสั่งซื้อ</MenuItem>
          <MenuItem value="returnedOrder">จำนวนคำสั่งซื้อที่ตีกลับ</MenuItem>
          <MenuItem value="returnedTotal">
            จำนวนยอดซื้อคำสั่งซื้อที่ตีกลับ
          </MenuItem>
        </TextField>
      </Grid>
      <Grid item xs={12} sm={4} lg={2}>
        <Button
          color="primary"
          size="large"
          fullWidth
          variant="contained"
          startIcon={<SearchIcon />}
          onClick={() => onSearch(filter)}
          sx={{ height: '100%' }}
        >
          ค้นหา
        </Button>
      </Grid>
      <Grid item xs={12} sm={4} lg={2}>
        <Button
          color="info"
          size="large"
          fullWidth
          variant="contained"
          disabled={
            !onDownloadExcel || downloadDisabled || isExporting
          }
          startIcon={
            isExporting ? (
              <CircularProgress color="inherit" size={22} />
            ) : (
              <FileDownloadOutlinedIcon />
            )
          }
          endIcon={!isExporting ? <ArrowDropDownIcon /> : undefined}
          onClick={handleOpenExportMenu}
          sx={{ height: '100%' }}
        >
          ดาวน์โหลด Excel
        </Button>
        <Menu
          anchorEl={exportAnchorEl}
          open={isExportMenuOpen}
          onClose={handleCloseExportMenu}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          <MenuItem
            onClick={() => handleSelectExport('customerWithOrders')}
          >
            รายชื่อลูกค้า (คำสั่งซื้อ)
          </MenuItem>
          <MenuItem onClick={() => handleSelectExport('customerList')}>
            รายชื่อลูกค้า
          </MenuItem>
        </Menu>
      </Grid>
      <Grid item xs={12} sm={4} lg={2}>
        <Button
          color="inherit"
          size="large"
          fullWidth
          variant="contained"
          startIcon={<ClearIcon />}
          onClick={() => handleReset()}
          sx={{ height: '100%' }}
        >
          รีเซ็ต
        </Button>
      </Grid>
    </Grid>
  )
}

export default CustomerReportFilter
